// ✅ ALTERED: HostelApplication UI to match your portal theme (Orange header + blue borders)
// - Outer Paper: borderRadius 2 + blue border + overflow hidden
// - Orange gradient header bar
// - Body BG = #F2F2F2
// - Surfaces: white + outlined blue borders
// - Inputs/Selects: consistent outlined borders + focus orange
// - Sections: left orange accent stripe + zebra tint + hover
// - Buttons: theme orange primary + optional outlined blue secondary
// NOTE: logic unchanged (only UI/theme + small layout polish)

import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "./config";
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Alert,
  Divider,
  Stack,
  Chip,
  FormControl,
  InputLabel,
} from "@mui/material";

const HostelApplication = () => {
  // ✅ Theme constants (same as your other pages)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const SURFACE_BORDER = "rgba(25,118,210,0.25)";

  const [StudentID, setStudentID] = useState("");
  const [studentkey, setStudentKey] = useState("");
  const [RoomPreference, setRoomPreference] = useState("");
  const [BlockPreference, setBlockPreference] = useState("");
  const [RoomID, setRoomID] = useState("");
  const [status, setStatus] = useState(null);

  const [rooms, setRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);

  const [logincampusCode, setCampusCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const token = localStorage.getItem("auth");

  // **Fetch and decode CampusCode from JWT**
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        const username = decodedToken.username;
        const loggedCampusCode = decodedToken.campusCode;
        setCampusCode(loggedCampusCode);
        setStudentID(username);
        setStudentKey(decodedToken.studentkey);
      } catch (err) {
        console.error("Error decoding token:", err);
        setError("Failed to decode authentication token");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // **Fetch rooms**
  useEffect(() => {
    if (!logincampusCode) return;
    const fetchAvailableRooms = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/availableRooms`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRooms(response.data || []);
      } catch (err) {
        console.error("Error fetching rooms:", err);
        setError("Failed to fetch hostel rooms");
      }
    };
    fetchAvailableRooms();
  }, [logincampusCode, token]);

  // **Filter rooms based on selected Block**
  useEffect(() => {
    if (BlockPreference) {
      setFilteredRooms(rooms.filter((room) => room.Block === BlockPreference));
      setRoomPreference("");
      setRoomID("");
    } else {
      setFilteredRooms([]);
    }
  }, [BlockPreference, rooms]);

  // **Update RoomID when RoomPreference changes**
  useEffect(() => {
    if (RoomPreference) {
      const selectedRoom = rooms.find((room) => room.RoomID === Number(RoomPreference));
      if (selectedRoom) setRoomID(selectedRoom.RoomID);
    }
  }, [RoomPreference, rooms]);

  // **Apply for Hostel**
  const applyForHostel = async () => {
    try {
      setError("");
      await axios.post(
        `${API_URL}/api/applyHostel`,
        {
          StudentID: StudentID,
          StudentKey: studentkey,
          RoomID: RoomID,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      checkApplicationStatus();
    } catch (error) {
      console.error("Error applying", error);
      if (error.response && error.response.status === 420) {
        setError(error.response.data.error || "Student has already applied for hostel.");
      } else {
        setError("An unexpected error occurred.");
      }
    }
  };

  // **Check Application Status**
  const checkApplicationStatus = async () => {
    try {
      setError("");
      const response = await axios.get(`${API_URL}/api/applicationStatus`, {
        params: { StudentID: StudentID, StudentKey: studentkey },
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(response.data);
    } catch (error) {
      console.error("Error fetching status", error);
      setError("Failed to fetch application status.");
    }
  };

  // ✅ common sx
  const surfacePaperSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${SURFACE_BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: SURFACE_BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(25,118,210,0.45)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(240,120,40,0.75)",
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const sectionSx = {
    ...surfacePaperSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const formatDT = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  };

  const selectedFee =
    RoomPreference && filteredRooms.length
      ? filteredRooms.find((r) => r.RoomID === Number(RoomPreference))?.HostelFee || ""
      : "";

  return (
    <Box sx={{ p: 2, backgroundColor: BG }}>
      <Paper
        elevation={5}
        sx={{
          mx: "auto",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid rgba(25,118,210,0.15)",
        }}
      >
        {/* ✅ Header bar */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, lineHeight: 1.2 }}>
              Hostel Application
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Choose block & room preference and submit your application
            </Typography>
          </Box>
        </Box>

        {/* ✅ Body */}
        <Box sx={{ p: 2, backgroundColor: BG }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" onClose={() => setSuccess(false)} sx={{ borderRadius: 2 }}>
                Hostel Application Submitted!
              </Alert>
            </Snackbar>
          )}

          {/* Application form */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Application Details
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    size="small"
                    label="Username"
                    value={StudentID}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Block Preference</InputLabel>
                    <Select
                      value={BlockPreference}
                      label="Block Preference"
                      onChange={(e) => setBlockPreference(e.target.value)}
                    >
                      <MenuItem value="" disabled>
                        Select Block Preference
                      </MenuItem>
                      {[...new Set((rooms || []).map((room) => room.Block))].map((block) => (
                        <MenuItem key={block} value={block}>
                          {block} - Block
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Room Preference</InputLabel>
                    <Select
                      value={RoomPreference}
                      label="Room Preference"
                      onChange={(e) => setRoomPreference(e.target.value)}
                      disabled={!BlockPreference}
                    >
                      <MenuItem value="" disabled>
                        Select Room Preference
                      </MenuItem>
                      {filteredRooms.map((room) => (
                        <MenuItem key={room.RoomID} value={room.RoomID}>
                          {room.Block} - Block {room.RoomNumber} ({room.RoomType}) (
                          {room.IsAirConditioned === "Yes" ? "AC" : "NonAC"})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    size="small"
                    label="Room Price"
                    value={selectedFee}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={applyForHostel}
                    fullWidth
                    sx={primaryBtnSx}
                    disabled={!RoomID}
                  >
                    Apply
                  </Button>
                  <Typography sx={{ mt: 1, opacity: 0.7, fontSize: "0.85rem" }}>
                    Tip: Select a block first, then pick a room.
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Application status */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  Application Status
                </Typography>

                <Button variant="contained" onClick={checkApplicationStatus} sx={primaryBtnSx}>
                  Check Status
                </Button>
              </Stack>

              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    label="Application Date"
                    value={status ? formatDT(status.ApplicationDate) : ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    label="Approved Date"
                    value={status ? formatDT(status.ApprovedDate) : ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    label="Application Status"
                    value={status ? status.Status : ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={1.71}>
                  <TextField
                    size="small"
                    label="Block"
                    value={status ? status.Block : ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={1.71}>
                  <TextField
                    size="small"
                    label="Room Number"
                    value={status ? status.RoomNumber : ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={1.71}>
                  <TextField
                    size="small"
                    label="Room Type"
                    value={status ? status.RoomType : ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={1.71}>
                  <TextField
                    size="small"
                    label="IsAirConditioned"
                    value={status ? status.IsAirConditioned : ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={1.71}>
                  <TextField
                    size="small"
                    label="Application Fee"
                    value={status ? status.HostelApplicationFee : ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={1.71}>
                  <TextField
                    size="small"
                    label="Hostel Deposit"
                    value={status ? status.HostelDeposit : ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={1.71}>
                  <TextField
                    size="small"
                    label="Hostel Fee"
                    value={status ? status.HostelFee : ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default HostelApplication;
